import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { normalizePhoneNumber } from 'helpers/rules'

// Helpers
import get from 'lodash/get'
import { number } from 'helpers/format'

// Components
import FormGroup from 'react-bootstrap/FormGroup'
import FormLabel from 'react-bootstrap/FormLabel'
import FormControl from 'react-bootstrap/FormControl'

// Styles
import './Input.scss'

export const Input = ({
  phoneNumber,
  name,
  label,
  placeholder,
  type,
  value,
  errors,
  readOnly,
  textarea,
  rows,
  maxlength,
  counter,
  append,
  autoComplete,
  resize,
  bottomLeft,
  onChange,
  onBlur,
  ...props
}) => {
  // Validate error
  const error = get(errors, name, null)

  const inputRef = useRef(null)

  // Counter text
  const counterText = useMemo(() => {
    const length = number(value ? value.length : 0)
    const suffix = typeof counter === 'string' ? counter : ''

    if (maxlength) {
      return `${length}/${number(maxlength)} ${suffix}`
    }

    return length
  }, [counter, maxlength, value])

  const handleChange = (value) => {
    if (phoneNumber) {
      value = normalizePhoneNumber(value)
    }

    if (maxlength) {
      value = value.slice(0, maxlength)
    }

    onChange(value, name)

    try {
      const input = inputRef.current
      const start = input.selectionStart
      const end = input.selectionEnd

      // Prevent if cursor is in the end
      if (start === end && end === input.value.length) {
        return
      }

      setTimeout(() => {
        input.setSelectionRange(start, end)
      }, 0)
    } catch (err) {
      //
    }
  }

  return (
    <FormGroup className="x-input">
      {!!label && <FormLabel>{label}</FormLabel>}

      <div className="position-relative">
        <FormControl
          ref={inputRef}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          isInvalid={!!error}
          readOnly={readOnly}
          plaintext={readOnly}
          as={textarea ? 'textarea' : 'input'}
          rows={rows}
          maxLength={maxlength}
          autoComplete={autoComplete}
          style={{ resize }}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={() => onBlur(name)}
          {...props}
        />
        <div className="append">{append}</div>
      </div>

      {!!bottomLeft && (
        <div className="input-meta">
          <div className="input-bottom-left input-error">{error}</div>
        </div>
      )}

      <div className="input-meta">
        <div className="input-bottom-left">
          {bottomLeft ? bottomLeft : <div className="input-error">{error}</div>}
        </div>

        {counter && <div className="input-counter">{counterText}</div>}
      </div>
    </FormGroup>
  )
}

Input.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.string,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  textarea: PropTypes.bool,
  rows: PropTypes.number,
  maxlength: PropTypes.number,
  append: PropTypes.node,
  counter: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  autoComplete: PropTypes.string,
  phoneNumber: PropTypes.bool,
  resize: PropTypes.string,

  bottomLeft: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),

  // Events
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
}

Input.defaultProps = {
  name: '',
  label: '',
  placeholder: '',
  type: 'text',
  value: '',
  errors: {},
  readOnly: false,
  textarea: false,
  rows: 3,
  maxlength: null,
  counter: false,
  autoComplete: 'off',
  append: null,
  phoneNumber: false,
  resize: 'none',

  bottomLeft: null,

  // Events
  onChange: () => {},
  onBlur: () => {},
}

export default Input
